import { ConfigurationVersion, gamificationApi } from "api/admin/gamification";
import { useState } from "react";
import { useQuery } from "react-query";
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  LoadingState,
  SectionCard,
  StatusPill,
  formatDate,
  formatLabel,
} from "./components";

const entityTypes = [
  "elo_mapping",
  "elo_tier",
  "streak_policy",
  "streak_milestone",
  "reward_mapping",
  "reward_definition",
];

export default function History() {
  const [entityType, setEntityType] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<ConfigurationVersion | undefined>();
  const limit = 25;
  const history = useQuery(
    ["gamification-history", entityType, offset],
    () =>
      gamificationApi.listConfigurationHistory({
        entityType: entityType || undefined,
        limit,
        offset,
      }),
    { retry: false, keepPreviousData: true },
  );

  return (
    <>
      <SectionCard
        title="Change history"
        description="See who changed a gamification rule, when it changed and why."
        action={
          <select
            aria-label="Filter change history"
            className="rounded-lg border-gray-300 text-sm focus:border-orange-400 focus:ring-orange-400"
            value={entityType}
            onChange={(event) => {
              setOffset(0);
              setEntityType(event.target.value);
            }}
          >
            <option value="">All changes</option>
            {entityTypes.map((type) => (
              <option key={type} value={type}>{formatLabel(type)}</option>
            ))}
          </select>
        }
      >
        {history.isLoading ? (
          <LoadingState rows={6} />
        ) : history.error ? (
          <ErrorState error={history.error} onRetry={() => history.refetch()} />
        ) : !history.data?.versions.length ? (
          <EmptyState
            title="No changes found"
            body="No gamification changes match this filter."
            icon="fa-clock-rotate-left"
          />
        ) : (
          <>
            <div className="divide-y divide-gray-100">
              {history.data.versions.map((version) => (
                <button
                  className="flex w-full flex-col gap-3 px-5 py-4 text-left transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-400 sm:flex-row sm:items-center sm:justify-between"
                  key={version.id}
                  onClick={() => setSelected(version)}
                  type="button"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-gray-950">
                        {formatLabel(version.entityType)}
                      </p>
                      <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
                        v{version.version}
                      </span>
                      <StatusPill status={version.lifecycleStatus} />
                    </div>
                    <p className="mt-1 truncate text-sm text-gray-500">
                      {version.changeReason || "No reason supplied"}
                    </p>
                    <p className="mt-1 truncate font-mono text-[11px] text-gray-400">
                      {version.configurationKey}
                    </p>
                  </div>
                  <div className="shrink-0 text-left sm:text-right">
                    <p className="text-sm font-medium text-gray-700">
                      {formatDate(version.createdAt)}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      Admin {version.changedByAdminId ?? "System"} ·{" "}
                      {version.market || "Global"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
              <p className="text-sm text-gray-500">
                {history.data.total} version{history.data.total === 1 ? "" : "s"}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  disabled={offset === 0}
                  onClick={() => setOffset((value) => Math.max(0, value - limit))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  disabled={offset + limit >= history.data.total}
                  onClick={() => setOffset((value) => value + limit)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </SectionCard>

      <Drawer
        open={Boolean(selected)}
        title={
          selected
            ? `${formatLabel(selected.entityType)} change`
            : "Rule change"
        }
        description={selected?.configurationKey}
        onClose={() => setSelected(undefined)}
      >
        {selected && (
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ["Lifecycle", formatLabel(selected.lifecycleStatus)],
                ["Market", selected.market || "Global"],
                ["Effective from", formatDate(selected.effectiveFrom)],
                ["Effective to", formatDate(selected.effectiveTo)],
                ["Changed by", selected.changedByAdminId ? `Admin ${selected.changedByAdminId}` : "System"],
                ["Recorded", formatDate(selected.createdAt)],
              ].map(([label, value]) => (
                <div className="rounded-xl border border-gray-200 p-4" key={label}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
                  <p className="mt-2 text-sm font-semibold text-gray-900">{value}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Change reason</p>
              <p className="mt-2 rounded-xl bg-gray-50 p-4 text-sm leading-6 text-gray-600">
                {selected.changeReason || "No reason supplied"}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Technical details</p>
              <pre className="mt-2 max-h-[420px] overflow-auto rounded-xl bg-gray-950 p-4 text-xs leading-6 text-gray-100">
                {JSON.stringify(selected.snapshot, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
}
